import config from "../../config"
import { useState, useEffect, useCallback, useMemo } from "react"
import {
  fetchAppManifest,
  fetchImdfGeojson,
  fetchMapLabels,
  fetchAppConfig,
  fetchMapDecorations,
} from "../../services/venue"
import { VenueContext } from "./VenueContext"
import _ from "lodash"
import { prepareFeatures, prepareMapDecorations } from "./utils/prepareFeature"
import { useShortestPath } from "./hooks/useShortestPath.ts"
import { useSearch } from "./hooks/useSearch.ts"
import { useTheme } from "./hooks/useTheme.ts"
import { useQuery } from "@tanstack/react-query"
import { prepareGeolocationFeature } from "providers/geolocation/utils/prepareGeolocationFeature"

import {
  createGeolocationFeature,
  isLatLngQueryString,
} from "utils/geolocation"
import {
  prepareMapConfig,
  prepareSearchConfig,
  prepareStepAutoplayConfig,
} from "./utils/prepareConfig"

const LOCALE_CONFIG = {
  en: {
    localeKey: "en",
    name: "English",
    flagCode: "gb",
    localizedName: "English",
  },
  th: {
    localeKey: "th",
    name: "Thai",
    flagCode: "th",
    localizedName: "ไทย",
  },
  zh: {
    localeKey: "zh",
    name: "Chinese",
    flagCode: "cn",
    localizedName: "Chinese",
  },
  ja: {
    localeKey: "ja",
    name: "Japanese",
    flagCode: "jp",
    localizedName: "Japanese",
  },
}

const DEFAULT_LOCALE_SUPPORT_LIST = ["en", "th"]

// TODO: Moved this config to app config and use it only as a default fall back
const DEFAULT_AMENITY_SORT_ORDER_CONFIG = [
  "information",
  "atm",
  "wheelchair",
  "restroom",
  "restroom.male",
  "restroom.female",
  "landmark",
  "parking",
  "parking.motorcycle",
  "taxi",
  "bus",
  "parking.bicycle",
  "smokingarea",
  "meetingpoint",
  "elevator",
  "escalator",
  "firstaid",
  "prayerroom",
  "stairs",
  "ticketing",
  "boardinggate.ferry",
]

export const defaultAppConfig = {
  map: {},
}

const initialData = {
  amenities: [],
  anchors: [],
  categories: [],
  events: [],
  features: [],
  levels: [],
  units: [],
  kiosks: [],
  fixtures: [],
  occupants: [],
  promotions: [],
  openings: [],
  relationships: [],
  sections: [],
  venues: [],
  privileges: [],
  footprints: [],
}

export const VenueProvider = ({ children }) => {
  /** State, Hooks */
  const device = config("device")
  const deviceType = /^kiosk/.test(device) ? "kiosk" : "mobile"

  const [appManifest, setAppManifest] = useState(null)

  /** 
   * Example Extension Configs
    {
      "extensions": {
          "app": [
            {
              "to": "promotions", // <-- path for registering route
              "component": "tourist-friendly/pages/promotions" // <-- component path for loading
            },
            ....
          ]
        }
      }
    }
  */

  const { data: imdfData, isFetched } = useQuery({
    queryKey: ["imdf"],
    queryFn: async () => {
      const imdfData = await fetchImdfGeojson()
      prepareFeatures(imdfData)
      return imdfData
    },
    initialData,
    retry: false,
    refetchOnWindowFocus: false,
  })

  const { data: appConfig, isFetched: isConfigFetched } = useQuery({
    queryKey: ["appConfig"],
    queryFn: async () => {
      try {
        const appConfig = await fetchAppConfig()
        return appConfig || defaultAppConfig
      } catch (err) {
        return defaultAppConfig
      }
    },
    initialData: defaultAppConfig,
    retry: false,
    refetchOnWindowFocus: false,
  })

  const mapConfig = useMemo(
    () => prepareMapConfig(_.get(appConfig, "map")),
    [appConfig]
  )

  const stepAutoplayConfig = useMemo(
    () => prepareStepAutoplayConfig(appConfig, deviceType),
    [appConfig, deviceType]
  )

  const searchConfig = useMemo(
    () => prepareSearchConfig(appConfig, deviceType),
    [appConfig, deviceType]
  )

  const { data: labels } = useQuery({
    queryKey: ["labels"],
    queryFn: fetchMapLabels,
    retry: false,
    refetchOnWindowFocus: false,
  })

  const { data: decorations, isFetched: isDecorationFetched } = useQuery({
    queryKey: ["elements"],
    queryFn: async () => {
      const decorationsData = await fetchMapDecorations()
      return prepareMapDecorations(decorationsData)
    },
    retry: false,
    refetchOnWindowFocus: false,
  })

  //correct locale properties is supported_locales
  const venueSupportedLocales = useMemo(
    () =>
      _.get(
        _.first(imdfData.venues),
        "properties.supported_locales",
        DEFAULT_LOCALE_SUPPORT_LIST
      ),
    [imdfData.venues]
  )

  const locales = useMemo(() => {
    try {
      return _(venueSupportedLocales)
        .map((locale) => _.get(LOCALE_CONFIG, locale))
        .compact()
        .value()
    } catch (error) {
      return _(DEFAULT_LOCALE_SUPPORT_LIST)
        .map((locale) => _.get(LOCALE_CONFIG, locale))
        .compact()
        .value()
    }
  }, [venueSupportedLocales])

  const themeName = config("themeName")
  const mapThemeName = config("mapThemeName")
  const { themeOptions, initVenueTheme, initMapTheme, mapTheme } = useTheme(
    themeName,
    mapThemeName
  )

  const { search, getSuggestedSearchItems } = useSearch(imdfData.features, {
    config: searchConfig,
    enable: isConfigFetched,
  })

  const {
    findRoute,
    findElevatorPreferredRoute,
    relationshipGraphLoaded,
    transformStepsToGeometries,
  } = useShortestPath(imdfData, { enabled: isFetched })

  const rootCategories = useMemo(
    () =>
      _.uniqBy(
        _.filter(
          imdfData.categories,
          (feature) => feature.properties.parent_id === null
        ),
        "id"
      ),
    [imdfData.categories]
  )

  const findFeatureById = useCallback(
    (id) => imdfData.features.find((f) => f.id === id),
    [imdfData]
  )

  const transformLocationToFeature = useCallback(
    (feature) => {
      return isLatLngQueryString(feature)
        ? prepareGeolocationFeature(
            createGeolocationFeature(feature, { name: "Your Location" }),
            { venues: imdfData.venues, levels: imdfData.levels }
          )
        : findFeatureById(feature) || null
    },
    [findFeatureById, imdfData.venues, imdfData.levels]
  )

  const getCategoriesFromAmenity = useCallback((amenities = []) => {
    return _(amenities)
      .uniqBy((amenity) => amenity.properties.category)
      .map((amenity) => amenity.properties.category)
      .orderBy([
        // First Sort by prioritize order from config
        (value) => {
          const indexOrder = DEFAULT_AMENITY_SORT_ORDER_CONFIG.findIndex(
            (category) => category === value
          )
          // If category not found it will be sent to last item
          return indexOrder !== -1 ? indexOrder : 9999
        },
        // When order is the same sort by alphabet
        (category) => category.toLowerCase(),
      ])
      .value()
  }, [])

  const amenityCategories = useMemo(
    () => getCategoriesFromAmenity(imdfData.amenities),
    [getCategoriesFromAmenity, imdfData.amenities]
  )

  const filterAmenitiesByCategory = useCallback(
    (category) => {
      return imdfData.amenities.filter(
        (amenityFeature) => amenityFeature.properties.category === category
      )
    },
    [imdfData.amenities]
  )

  const filterOccupantsByRootCategory = useCallback(
    (category) => {
      return imdfData.occupants.filter(
        ({ feature_type, properties }) =>
          feature_type === "occupant" &&
          properties.anchor &&
          (category === undefined ||
            properties.local_parent_categories?.[0]?.id === category)
      )
    },
    [imdfData.occupants]
  )

  const isMultiOrdinalVenue = useMemo(() => {
    return _.uniqBy(imdfData.levels, "properties.ordinal").length > 1
  }, [imdfData.levels])

  const isMultiVenueProject = useMemo(() => {
    return imdfData.venues.length > 1
  }, [imdfData.venues])

  // TODO: Find and Implement default Venue
  const defaultVenue = useMemo(() => _.first(imdfData.venues), [imdfData])

  const initVenue = useCallback(async () => {
    const manifest = await fetchAppManifest()
    setAppManifest(manifest)

    initVenueTheme()
    initMapTheme()
  }, [initVenueTheme, initMapTheme])

  /**
   * Side Effect when manifest is updated
   */
  useEffect(() => {
    // Set favicon
    const favicon = appManifest?.icons?.find(
      (icon) => icon.type === "image/x-icon"
    )
    if (favicon) {
      document.getElementById("favicon").href = favicon.src
    }
  }, [appManifest])

  // fetch ข้อมูลเริ่มต้นลง feature state และ theme
  useEffect(() => {
    try {
      initVenue()
    } catch (err) {
      throw err
    }
  }, [initVenue])

  const value = {
    ...imdfData,
    labels,
    decorations,
    themeOptions,
    mapTheme,
    amenityCategories,
    locales,
    rootCategories,
    findRoute,
    isMultiOrdinalVenue,
    isMultiVenueProject,
    findElevatorPreferredRoute,
    appConfig,
    stepAutoplayConfig,
    defaultVenue,
    // Flag
    relationshipGraphLoaded,
    dataLoaded: isFetched && isDecorationFetched && isConfigFetched,
    mapConfig,
    // functions
    findFeatureById,
    search,
    filterAmenitiesByCategory,
    filterOccupantsByRootCategory,
    transformLocationToFeature,
    transformStepsToGeometries,
    getCategoriesFromAmenity,
    getSuggestedSearchItems,
  }

  return <VenueContext.Provider value={value}>{children}</VenueContext.Provider>
}
